import { ExternalLink } from 'lucide-react';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  CopyButton,
  Input,
  MarkdownContent,
} from '@/components/ui';
import type {
  McpAuthMethod,
  McpProviderCatalogEntry,
  McpScopeOption,
} from '../../../types/mcp';
import McpQuickSetupPanel from './McpQuickSetupPanel';

export function resolveTemplateUrl(
  provider: McpProviderCatalogEntry,
  values: Record<string, string>,
): string {
  if (typeof provider.serverUrl === 'string') return provider.serverUrl;
  return provider.serverUrl.template.replace(
    /\{(\w+)\}/g,
    (_, name) => values[name] ?? `{${name}}`,
  );
}

interface McpCatalogFormFieldsProps {
  provider: McpProviderCatalogEntry;
  /** The method picked upstream (auto-selected when authMethods.length === 1). */
  method: McpAuthMethod;
  ourCallbackUrl: string;
  templateValues: Record<string, string>;
  setTemplateValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  selectedScopes: string[];
  setSelectedScopes: React.Dispatch<React.SetStateAction<string[]>>;
  manualClientId: string;
  setManualClientId: (v: string) => void;
  manualClientSecret: string;
  setManualClientSecret: (v: string) => void;
  /** Header values keyed by spec.name; only consumed for api-key methods. */
  headerValues: Record<string, string>;
  setHeaderValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

export function McpCatalogFormFields({
  provider,
  method,
  ourCallbackUrl,
  templateValues,
  setTemplateValues,
  selectedScopes,
  setSelectedScopes,
  manualClientId,
  setManualClientId,
  manualClientSecret,
  setManualClientSecret,
  headerValues,
  setHeaderValues,
}: McpCatalogFormFieldsProps) {
  const isTemplated = typeof provider.serverUrl !== 'string';
  const resolvedUrl = resolveTemplateUrl(provider, templateValues);

  // Only the OAuth (dcr / manual-client) methods carry a scopes spec.
  // `'scopes' in m` narrows away api-key and oauth-manual cleanly.
  const scopesSpec = 'scopes' in method ? method.scopes : undefined;
  const scopeOptions: McpScopeOption[] = scopesSpec?.available
    ? [...scopesSpec.available]
    : [];
  // Show the scope picker only for OAuth methods that advertise a full
  // `available` list — without it the runtime falls back to OAuth-time
  // discovery and there's nothing meaningful to tick.
  const showScopesCard = scopeOptions.length > 0;

  const toggleScope = (scope: string) =>
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  const selectAllScopes = () =>
    setSelectedScopes(scopeOptions.map((s) => s.value));
  const clearAllScopes = () => setSelectedScopes([]);

  return (
    <div className="space-y-6">
      <McpQuickSetupPanel
        markdown={method.quickSetupGuideMarkdown}
        copyableValues={
          method.kind === 'api-key' ? [] : [ourCallbackUrl]
        }
      />

      <Card>
        <CardHeader
          title="Server"
          description="Endpoint connection details. Pre-filled from the catalog — adjust if your deployment differs."
        />
        {isTemplated && provider.serverUrl !== null && typeof provider.serverUrl !== 'string' ? (
          <div className="space-y-4">
            {provider.serverUrl.variables.map((variable) => {
              const id = `var-${variable.name}`;
              const value = templateValues[variable.name] ?? '';
              if (variable.enum && variable.enum.length > 0) {
                return (
                  <div key={variable.name}>
                    <label
                      htmlFor={id}
                      className="block text-sm font-medium text-foreground mb-1"
                    >
                      {variable.label}
                      {variable.required && (
                        <span className="text-red-600 ml-0.5">*</span>
                      )}
                    </label>
                    <select
                      id={id}
                      value={value}
                      onChange={(e) =>
                        setTemplateValues((prev) => ({
                          ...prev,
                          [variable.name]: e.target.value,
                        }))
                      }
                      required={variable.required}
                      className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
                    >
                      {!variable.required && !variable.default && (
                        <option value="">Select…</option>
                      )}
                      {variable.enum.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }
              return (
                <Input
                  key={variable.name}
                  id={id}
                  label={variable.label}
                  value={value}
                  onChange={(e: { target: { value: string } }) =>
                    setTemplateValues((prev) => ({
                      ...prev,
                      [variable.name]: e.target.value,
                    }))
                  }
                  required={variable.required}
                />
              );
            })}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Server URL
              </label>
              <code className="block w-full rounded-md border border-border bg-muted px-3 py-2 text-xs font-mono break-all text-muted-foreground">
                {resolvedUrl}
              </code>
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Server URL
            </label>
            <code className="block w-full rounded-md border border-border bg-muted px-3 py-2 text-xs font-mono break-all text-muted-foreground">
              {resolvedUrl}
            </code>
          </div>
        )}
      </Card>

      {method.kind === 'api-key' && (
        <Card>
          <CardHeader
            title="API keys"
            description={`${provider.displayName} authenticates with one or more API keys sent as request headers. Paste each value below.`}
          />
          <div className="space-y-4">
            {method.headers.map((header) => {
              const id = `mcp-header-${header.name}`;
              return (
                <div key={header.name} className="space-y-1.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <label
                      htmlFor={id}
                      className="block text-sm font-medium text-foreground"
                    >
                      {header.label}
                      <span className="text-red-600 ml-0.5">*</span>
                    </label>
                    {header.obtainUrl && (
                      <a
                        href={header.obtainUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-500"
                      >
                        Where to get this <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <Input
                    id={id}
                    type="password"
                    value={headerValues[header.name] ?? ''}
                    onChange={(e: { target: { value: string } }) =>
                      setHeaderValues((prev) => ({
                        ...prev,
                        [header.name]: e.target.value,
                      }))
                    }
                    placeholder={`Paste ${header.label}`}
                    autoComplete="off"
                    required
                  />
                  <p className="text-xs text-muted-foreground font-mono">
                    Sent as <code>{header.name}</code>.
                  </p>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {method.kind === 'oauth-manual-client' && (
        <Card>
          <CardHeader
            title="Register an OAuth client"
            description={`${provider.displayName} requires a pre-registered OAuth client. Create one and supply its credentials below.`}
          />
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/40 p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground font-medium">
                    1. Open the registration page
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create a new OAuth app at {provider.displayName}.
                  </p>
                </div>
                <a
                  href={method.oauthAppRegistrationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button type="button" variant="secondary" size="sm">
                    Open registration
                    <ExternalLink className="h-3 w-3 ml-1.5" />
                  </Button>
                </a>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-sm text-foreground font-medium">
                  2. Whitelist this redirect URI
                </p>
                <p className="text-xs text-muted-foreground mt-1 mb-2">
                  Add this exact URL as an allowed redirect URI on your OAuth
                  app.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-xs font-mono break-all">
                    {ourCallbackUrl}
                  </code>
                  <CopyButton value={ourCallbackUrl} label="Copy" size="md" />
                </div>
              </div>
            </div>
            <Input
              label="Client ID"
              value={manualClientId}
              onChange={(e: { target: { value: string } }) =>
                setManualClientId(e.target.value)
              }
              placeholder="OAuth client id"
              autoComplete="off"
              required
            />
            <Input
              label="Client Secret"
              type="password"
              value={manualClientSecret}
              onChange={(e: { target: { value: string } }) =>
                setManualClientSecret(e.target.value)
              }
              placeholder="OAuth client secret"
              autoComplete="new-password"
              required
            />
          </div>
        </Card>
      )}

      {showScopesCard && (
        <Card>
          <CardHeader
            title="Scopes"
            description="OAuth scopes requested during consent. Recommended scopes are pre-selected — tick any extras you need."
          />
          <div className="flex items-center justify-end mb-2">
            <div className="flex gap-3 text-xs">
              <button
                type="button"
                onClick={selectAllScopes}
                className="text-primary-600 hover:text-primary-500"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearAllScopes}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear all
              </button>
            </div>
          </div>
          <div className="rounded-md border border-border divide-y">
            {scopeOptions.map((scope) => {
              const id = `scope-${scope.value}`;
              const checked = selectedScopes.includes(scope.value);
              return (
                <label
                  key={scope.value}
                  htmlFor={id}
                  className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-accent/50 transition-colors"
                >
                  <Checkbox
                    id={id}
                    checked={checked}
                    onCheckedChange={() => toggleScope(scope.value)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-foreground break-all">
                      {scope.value}
                    </div>
                    {scope.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {scope.description}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </Card>
      )}

      {method.setupGuideMarkdown && (
        <Card>
          <CardHeader title="Setup guide" />
          <MarkdownContent
            size="sm"
            copyableValues={method.kind === 'api-key' ? [] : [ourCallbackUrl]}
          >
            {method.setupGuideMarkdown}
          </MarkdownContent>
        </Card>
      )}
    </div>
  );
}
